import {
  DNA_SOURCES, PUBLICOS, STUDIO_TABS, STUDIO_TOOLS, TOM_LABEL, TONS, briefingSchema, studioCategoryLabel, studioToolKeySchema,
  type Briefing, type StudioCategory, type StudioToolKey, type StudioToolMeta,
} from '@ads/shared';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { SelectField, TextArea, TextField } from '../components/Field';
import * as Ic from '../components/icons';
import { Seg } from '../components/Seg';
import { Actions, useStage, useViewMeta } from '../components/Shell';
import { MARKS, PREVIEWS } from '../components/studioArt';
import { Tabs } from '../components/Tabs';
import { Doc, useDsStyle } from '../doc/Doc';
import { PublishSheet } from '../site/Publish';
import { SiteCanvas } from '../site/SiteCanvas';
import { dateLong, dateTime } from '../lib/format';
import { useArtifacts, useDesignSystem, useDna, useMaterials, useSession } from '../lib/queries';
import { useGeneration, type GenState } from '../lib/useGeneration';

/* ─────────────────────────────────────────────────────────────────────
   Studio — o rack de nove agentes e a bancada de cada um.
   A bancada nasce do retângulo do ladrilho: é o mesmo objeto crescendo.
   Dentro dela acontece o momento de gerar.
   ───────────────────────────────────────────────────────────────────── */

/** Retângulo do ladrilho, relativo ao topo da tela do Studio. */
type Rect = { wt: number; wl: number; wr: number; bottom: number };

export function Studio() {
  const { tool } = useParams();
  const parsed = studioToolKeySchema.safeParse(tool);
  const active = parsed.success ? parsed.data : null;
  const meta = active ? STUDIO_TOOLS.find(t => t.key === active)! : null;
  useViewMeta({ nav: 'studio', crumb: ['Studio', meta ? meta.name : 'Nove agentes de produção'], j: .80 });

  const nav = useNavigate();
  const loc = useLocation();
  const stage = useStage();
  const dna = useDna();
  const { me } = useSession();
  const section = useRef<HTMLElement>(null);
  const [tab, setTab] = useState<StudioCategory | 'todas'>('todas');
  const [closing, setClosing] = useState(false);
  const origin = (loc.state as { origin?: Rect } | null)?.origin ?? null;

  const rectOf = (el: Element): Rect | null => {
    const host = section.current?.getBoundingClientRect();
    if (!host) return null;
    const r = el.getBoundingClientRect();
    return { wt: r.top - host.top, wl: r.left - host.left, wr: host.right - r.right, bottom: r.bottom - host.top };
  };

  /* a bancada nasce do retângulo do ladrilho — é o mesmo objeto crescendo */
  const open = (key: StudioToolKey, el: Element) => {
    const r = stage.current && stage.current.scrollTop > 0 ? null : rectOf(el);
    nav(`/studio/${key}`, { state: { origin: r } });
  };
  const close = () => {
    if (!active) return;
    if (!origin || matchMedia('(prefers-reduced-motion: reduce)').matches) { nav('/studio'); return; }
    stage.current?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    setClosing(true);
    setTimeout(() => { setClosing(false); nav('/studio'); }, 370);
  };

  const coverage = dna.data?.content.coverage ?? {};
  const visible = STUDIO_TOOLS.filter(t => tab === 'todas' || t.category === tab);

  return (
    <section className="view on" data-v="studio" ref={section}>
      {active && meta ? (
        <Workbench key={active} meta={meta} origin={origin} closing={closing} onClose={close} />
      ) : (
        <>
          <div className="vhead">
            <div>
              <div className="eyebrow"><span className="rule" /><span>Studio</span><b>9 ferramentas</b></div>
              <h1>Studio</h1>
              <p className="lede">Nove agentes de produção. Cada um abre a própria bancada, já abastecida pelo DNA da {me.org.name} — nenhum começa do zero.</p>
            </div>
            <div className="tools"><Tabs options={STUDIO_TABS} value={tab} onChange={setTab} label="Categorias" /></div>
          </div>

          <div className="rack">
            {visible.map(t => <Tile key={t.key} t={t} coverage={coverage} onOpen={open} />)}
          </div>

          <Actions>
            <button className="btn-ghost" type="button" onClick={() => nav('/biblioteca')}>Ver biblioteca</button>
            <button className="btn-primary" type="button" onClick={() => {
              const tile = document.querySelector('.tile[data-tool="plan"]');
              if (tile) open('plan', tile); else nav('/studio/plan');
            }}>Abrir Planejamento Estratégico<Ic.ArrowRight /></button>
          </Actions>
        </>
      )}
    </section>
  );
}

function Tile({ t, coverage, onOpen }: { t: StudioToolMeta; coverage: Record<string, number>; onOpen: (k: StudioToolKey, el: Element) => void }) {
  const uses = t.dna.map(k => ({ label: DNA_SOURCES[k].label, partial: (coverage[k] ?? 1) < 1 }));
  return (
    <div className={`slot${t.hero ? ' hero' : ''}`}>
      <button className={`tile${t.hero ? ' hero' : ''}`} type="button" data-tool={t.key} onClick={e => onOpen(t.key, e.currentTarget)}>
        <span className="mk-wrap">
          <svg className="mk" viewBox="0 0 64 64" dangerouslySetInnerHTML={{ __html: MARKS[t.key] }} />
          {t.hero && <span className="sug">Sugerido agora</span>}
        </span>
        <span className="tl-body">
          <span className="tl-no">{t.no}<em>{studioCategoryLabel(t.category)}</em></span>
          <span className="tl-name">{t.name}</span>
          <span className="tl-desc">{t.desc}</span>
        </span>
        <span className="tl-more">
          <span className="tl-uses"><em>Usa do DNA</em>
            {uses.slice(0, 3).map(u => <i key={u.label} className={u.partial ? 'pt' : ''}>{u.label}</i>)}
            {uses.length > 3 && <i className="more">+{uses.length - 3}</i>}
          </span>
          <span className="tl-out"><em>Entrega</em><b>{t.ext}</b><s>·</s><b>{t.formats}</b></span>
        </span>
        <span className="tl-foot"><span className="tl-cost">{t.cost} tokens · {t.ext}</span>
          <span className="tl-go">Abrir bancada<Ic.ArrowRight /></span></span>
      </button>
    </div>
  );
}

/* ── a bancada ─────────────────────────────────────────────────────── */

const CTA: Record<StudioToolKey, string> = {
  plan: 'Gerar o planejamento', acao: 'Gerar o plano de ação', apres: 'Gerar a apresentação',
  landing: 'Gerar a landing page', site: 'Gerar o site', brand: 'Gerar o brand book',
  manif: 'Gerar o manifesto', ebook: 'Gerar o e-book', camp: 'Gerar a campanha',
};

const DEFAULT_OBJECTIVE: Record<StudioToolKey, string> = {
  plan: 'Prestação de contas ao conselho', acao: 'Colocar as prioridades do ciclo em movimento',
  apres: 'Apresentar a escola a novos parceiros', landing: 'Converter visitas em matrículas',
  site: 'Reestruturar o site institucional', brand: 'Padronizar a aplicação da marca',
  manif: 'Dar voz ao propósito', ebook: 'Educar e atrair famílias', camp: 'Tornar a proposta pedagógica conhecida',
};

const BRIEF_KEY = (k: string) => `ads:briefing:${k}`;
const loadBrief = (k: StudioToolKey): Briefing => {
  const fallback = briefingSchema.parse({ objective: DEFAULT_OBJECTIVE[k] });
  try {
    const raw = localStorage.getItem(BRIEF_KEY(k));
    return raw ? briefingSchema.parse(JSON.parse(raw)) : fallback;
  } catch { return fallback; }
};

function Workbench({ meta, origin, closing, onClose }: { meta: StudioToolMeta; origin: Rect | null; closing: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const dna = useDna();
  const ds = useDesignSystem();
  const artifacts = useArtifacts();
  const { me } = useSession();
  const gen = useGeneration(meta.key);
  const st = gen.state;
  useDsStyle(ds.data);
  const [brief, setBrief] = useState<Briefing>(() => loadBrief(meta.key));
  const [saved, setSaved] = useState(false);
  const resumed = useRef(false);
  const isPage = meta.output === 'page';
  const linkError = brief.link && !/^(https?:\/\/\S+|mailto:\S+|tel:[+\d\s()-]+|#[\w-]*)$/i.test(brief.link) ? 'Use um link que comece com https://, mailto: ou tel:' : null;
  const [sitePage, setSitePage] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  const coverage = dna.data?.content.coverage ?? {};
  const alignment = Math.round((meta.dna.reduce((s, k) => s + (coverage[k] ?? 0), 0) / meta.dna.length) * 100);
  const versions = (artifacts.data ?? []).filter(a => a.tool === meta.key);
  const credits = me.org.credits;
  const enough = credits.quota - credits.used >= meta.cost;

  /* voltou para a bancada com uma geração ainda em curso: reconecta */
  useEffect(() => {
    if (resumed.current || !artifacts.data) return;
    resumed.current = true;
    const live = artifacts.data.find(a => a.tool === meta.key && a.status === 'generating');
    if (live) gen.resume(live.id);
  }, [artifacts.data, meta.key, gen]);

  const style = origin ? ({ '--wt': `${origin.wt}px`, '--wl': `${origin.wl}px`, '--wr': `${origin.wr}px`, '--wb': `calc(100% - ${origin.bottom}px)` } as CSSProperties) : undefined;
  const cls = closing ? 'work closing' : origin ? 'work opening' : 'work opening-plain';
  const running = st.status === 'running';
  const mode = st.status === 'idle' ? 'brief' : 'gen';

  const grid = useRef<HTMLDivElement>(null);
  const stage = useStage();
  const go = () => {
    try { localStorage.setItem(BRIEF_KEY(meta.key), JSON.stringify(brief)); } catch { /* ok */ }
    gen.start(brief, meta.cost);
    /* o palco sobe até a bancada: a peça ocupa a tela inteira enquanto nasce */
    const g = grid.current, st = stage.current;
    if (g && st) st.scrollTo({ top: g.getBoundingClientRect().top - st.getBoundingClientRect().top + st.scrollTop - 16, behavior: 'smooth' });
  };

  return (
    <div className={cls} style={style} data-mode={mode} data-status={st.status}>
      <div className="work-inner">
        <div className="work-top">
          <button className="back" type="button" onClick={onClose}><Ic.ChevronLeft />Todas as ferramentas</button>
          <div className="work-id">
            <span className="work-mk"><svg className="mk" viewBox="0 0 64 64" dangerouslySetInnerHTML={{ __html: MARKS[meta.key] }} /></span>
            <div>
              <div className="work-no">{meta.no} · {studioCategoryLabel(meta.category)}</div>
              <h2>{meta.name}</h2>
            </div>
          </div>
          <p className="work-desc">{meta.desc}</p>
        </div>

        <div className="work-grid" ref={grid}>
          <div className="work-brief">
            <div className="fam-h">Briefing</div>
            <fieldset disabled={running} className="brief-set">
              <TextField id="wObj" label="Objetivo desta entrega" value={brief.objective} maxLength={200}
                onChange={v => setBrief(b => ({ ...b, objective: v }))} />
              {isPage && (
                <TextField id="wLink" label="Para onde vai o botão principal" value={brief.link} maxLength={400}
                  placeholder="https://wa.me/55… · mailto:contato@… · link do formulário"
                  hint={linkError ?? 'WhatsApp, formulário, agenda ou e-mail. Em branco, o botão leva ao fim da página.'}
                  invalid={!!linkError}
                  onChange={v => setBrief(b => ({ ...b, link: v.trim() }))} />
              )}
              <SelectField id="wPub" label="Para quem" value={brief.audience} options={PUBLICOS} placeholder="Selecione"
                onChange={v => setBrief(b => ({ ...b, audience: (v || 'Conselho diretor') as Briefing['audience'] }))} />
              <div className="f"><span className="lab" id="lblTom">Tom</span>
                <Seg labelledBy="lblTom" value={brief.tone} options={TONS.map(t => ({ value: t, label: TOM_LABEL[t] }))}
                  onChange={v => setBrief(b => ({ ...b, tone: v }))} /></div>
              <TextArea id="wNota" label="Instruções livres" value={brief.notes} maxLength={1200} minLines={4}
                placeholder="Ex.: levar os sete indicadores do Mapa de Gaps para um anexo ao final."
                onChange={v => setBrief(b => ({ ...b, notes: v }))} />
            </fieldset>
            {ds.data && (
              <div className="ds-badge" title={`Versão ${ds.data.hash}`}>
                <span className="ds-sw" data-ds={ds.data.hash}><i className="p" /><i className="a" /><i className="h" /></span>
                <span><b>{ds.data.name} {ds.data.version}</b>A peça sai no design system do cliente</span>
              </div>
            )}
          </div>

          {mode === 'brief' ? (
            <div className="bench-stage">
              <div className="paper swap">
                <div className="pv-frame">
                  <div className="pv on"><svg viewBox="0 0 240 180" dangerouslySetInnerHTML={{ __html: PREVIEWS[meta.key] }} /></div>
                </div>
                <div className="pv-shadow" aria-hidden="true" />
              </div>
              <div className="pv-meta">
                <h4>{meta.deliverable}</h4>
                <p>{meta.desc}</p>
              </div>
            </div>
          ) : (
            <GenStage meta={meta} st={st} orgName={me.org.name} link={brief.link} page={sitePage} onPage={setSitePage} />
          )}

          <Feed meta={meta} st={st} brief={brief} coverage={coverage} alignment={alignment} versions={versions.slice(0, 3)} dsName={ds.data ? `${ds.data.name} ${ds.data.version}` : null}
            onSection={isPage ? setSitePage : undefined} />
        </div>
      </div>

      <Actions>
        {st.status === 'idle' && <>
          <button className="btn-ghost" type="button" onClick={() => {
            try { localStorage.setItem(BRIEF_KEY(meta.key), JSON.stringify(brief)); } catch { /* ok */ }
            setSaved(true); setTimeout(() => setSaved(false), 1600);
          }}>{saved ? 'Briefing salvo' : 'Salvar briefing'}</button>
          {dna.isFetched && !dna.data ? (
            /* sem DNA não há o que ler: o caminho é consolidar primeiro */
            <button className="btn-primary" type="button" onClick={() => nav('/dna')}>
              Consolidar o DNA primeiro<Ic.ArrowRight />
            </button>
          ) : (
            <button className="btn-primary" type="button" disabled={!enough || !dna.data || !!linkError} onClick={go}
              title={!enough ? 'Saldo de tokens insuficiente' : undefined}>
              {enough ? CTA[meta.key] : 'Saldo insuficiente'}
              <Ic.ArrowRight />
            </button>
          )}
        </>}
        {running && <>
          <button className="btn-ghost" type="button" onClick={() => void gen.cancel()}>Cancelar</button>
          <button className="btn-primary is-busy" type="button" disabled><span className="spin" />{st.phaseLabel || 'Gerando'}…</button>
        </>}
        {st.status === 'done' && !isPage && <>
          <button className="btn-ghost" type="button" onClick={gen.reset}>Gerar outra versão</button>
          <button className="btn-primary" type="button" onClick={() => nav(`/pecas/${st.artifactId}`)}>Abrir documento<Ic.ArrowRight /></button>
        </>}
        {st.status === 'done' && isPage && <>
          <button className="btn-ghost" type="button" onClick={gen.reset}>Gerar outra versão</button>
          <button className="btn-ghost" type="button" onClick={() => nav(`/pecas/${st.artifactId}`)}>Abrir na biblioteca</button>
          <button className="btn-primary" type="button" onClick={() => setPublishing(true)}><span className="with-ic"><Ic.Rocket />Aprovar e publicar</span></button>
        </>}
        {st.status === 'failed' && <>
          <button className="btn-ghost" type="button" onClick={gen.reset}>Voltar ao briefing</button>
          <button className="btn-primary" type="button" onClick={go} disabled={!enough}>Tentar de novo<Ic.Refresh /></button>
        </>}
      </Actions>
      {publishing && st.artifactId && (
        <PublishSheet artifactId={st.artifactId} title={st.title ?? meta.deliverable} tool={meta.key} onClose={() => setPublishing(false)} />
      )}
    </div>
  );
}

/* ── o palco durante a geração: a peça se montando ─────────────────── */

function GenStage({ meta, st, orgName, link, page, onPage }: {
  meta: StudioToolMeta; st: GenState; orgName: string; link: string; page: string | null; onPage: (s: string) => void;
}) {
  const ds = useDesignSystem();
  if (meta.output === 'page') {
    return (
      <div className="gen-stage site">
        {st.status === 'failed' && !st.blocks.length ? (
          <div className="gen-fail"><Ic.Warning /><b>A geração não terminou.</b><p>{st.error}</p></div>
        ) : (
          <SiteCanvas title={st.title ?? meta.deliverable} orgName={orgName} blocks={st.blocks} ds={ds.data}
            primaryLink={link} liveFrom={st.liveFrom} running={st.status === 'running'} page={page} onPage={onPage}
            openHref={st.status === 'done' && st.artifactId ? `/api/artifacts/${st.artifactId}/site/` : null} />
        )}
      </div>
    );
  }
  return <DocStage meta={meta} st={st} orgName={orgName} ds={ds.data} />;
}

function DocStage({ meta, st, orgName, ds }: { meta: StudioToolMeta; st: GenState; orgName: string; ds: ReturnType<typeof useDesignSystem>['data'] }) {
  const scroller = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  const [zoom, setZoom] = useState(1);
  /* a página tem largura de página (760px) e encolhe para caber, sem refluir o texto */
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const fit = () => setZoom(Math.min(1, (el.clientWidth - 48) / 760));
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const today = useMemo(() => dateLong(new Date().toISOString()), []);

  /* acompanha a escrita, a menos que a pessoa tenha subido para reler */
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !follow.current || st.status !== 'running') return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [st.blocks.length, st.status]);

  return (
    <div className="gen-stage">
      <div className="gen-scroll" ref={scroller}
        onScroll={e => { const el = e.currentTarget; follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 140; }}>
        {st.status === 'failed' && !st.blocks.length ? (
          <div className="gen-fail"><Ic.Warning /><b>A geração não terminou.</b><p>{st.error}</p></div>
        ) : (
          <div className="gen-paper" style={{ zoom }}>
            <Doc title={st.title ?? meta.deliverable} org={orgName} deliverable={meta.deliverable} date={today}
              blocks={st.blocks} ds={ds} liveFrom={st.liveFrom}
              writing={st.status === 'running' && st.blocks.length > 0} pending={st.status === 'running' && !st.blocks.length} />
            {st.status === 'failed' && <div className="gen-fail inline"><Ic.Warning /><p>{st.error}</p></div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── a coluna da direita: de onde vem, por onde vai ────────────────── */

const PHASES: Array<[NonNullable<GenState['phase']>, string]> = [
  ['reading', 'Lendo o DNA'], ['outlining', 'Estruturando'], ['writing', 'Escrevendo'], ['finishing', 'Revisando'],
];

function Feed({ meta, st, brief, coverage, alignment, versions, dsName, onSection }: {
  meta: StudioToolMeta; st: GenState; brief: Briefing; coverage: Record<string, number>; alignment: number;
  versions: Array<{ id: string; createdAt: string; status: string }>; dsName: string | null;
  onSection?: (slug: string) => void;
}) {
  const nav = useNavigate();
  const mats = useMaterials();
  const readMats = (mats.data?.items ?? []).filter(m => m.reading === 'read' && m.catalog);
  const idle = st.status === 'idle';
  const readIndex = st.phasesSeen.includes('outlining') || st.status !== 'running' ? Infinity : st.phase === 'reading' ? -1 : -2;
  const sections = st.outline?.sections ?? [];
  const isPage = meta.output === 'page';
  const pages = st.blocks.filter(b => b.type === 'page') as Array<{ id: string; slug: string }>;
  const current = [...st.blocks].reverse().find(b => b.type === (isPage && pages.length ? 'page' : 'section')) as { id: string } | undefined;
  /* landing: uma faixa por seção do esboço */
  const curIdx = current ? sections.findIndex(s => s.id === current.id)
    : isPage && st.blocks.length ? Math.min(st.blocks.length, sections.length) - 1 : -1;
  const unit = meta.key === 'site' ? 'página' : isPage ? 'faixa' : 'seção';
  const [, tick] = useState(0);
  useEffect(() => {
    if (st.status !== 'running') return;
    const t = setInterval(() => tick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [st.status]);
  const elapsed = st.startedAt ? Math.max(0, Math.round(((st.finishedAt ?? Date.now()) - st.startedAt) / 1000)) : 0;

  return (
    <div className="bench-feed">
      {!idle && (
        <div className="brief-sum">
          <div className="fam-h sm">Briefing</div>
          <b>{brief.objective || 'Sem objetivo definido'}</b>
          <span>{brief.audience} · tom {TOM_LABEL[brief.tone].toLowerCase()}{brief.notes ? ' · com instruções' : ''}</span>
        </div>
      )}
      <div className="fam-h">O que o DNA entrega</div>
      <ul className={`feed${!idle ? ' gen' : ''}`} data-reading={readIndex === -1 || undefined}>
        {meta.dna.map((k, i) => {
          const partial = (coverage[k] ?? 1) < 1;
          return (
            <li key={k} className={`${partial ? 'partial' : ''}${!idle ? ' lit' : ''}`} style={{ '--i': i } as CSSProperties}>
              <span className="dot" />{DNA_SOURCES[k].label}{partial && <em>parcial</em>}
            </li>
          );
        })}
      </ul>
      {readMats.length > 0 && (
        <>
          <div className="fam-h">Materiais da organização</div>
          <ul className={`feed mats-feed${!idle ? ' gen' : ''}`} data-reading={readIndex === -1 || undefined}>
            {readMats.slice(0, 6).map((m, i) => (
              <li key={m.id} className={!idle ? 'lit' : ''} style={{ '--i': meta.dna.length + i } as CSSProperties} title={`${m.catalog!.title} — ${m.catalog!.summary}`}>
                <span className="dot" /><span className="mf-name">{m.catalog!.title}</span><em>{m.catalog!.category}</em>
              </li>
            ))}
            {readMats.length > 6 && <li className="partial"><span className="dot" />e mais {readMats.length - 6}</li>}
          </ul>
        </>
      )}

      {idle ? (
        <>
          <div className="feed-facts">
            <div><span>Extensão</span><b>{meta.ext}</b></div>
            <div><span>Custo</span><b>{meta.cost} tokens</b></div>
            <div><span>Formatos</span><b>{meta.formats}</b></div>
          </div>
          <div className="feed-align">
            <div className="bar" style={{ gridTemplateColumns: 'minmax(0,1fr) 0 42px', gap: 10 }}>
              <span className="l">Alinhamento ao DNA</span><span /><span className="n">{alignment}</span>
            </div>
            <span className="align-t"><i style={{ width: `${alignment}%` }} /></span>
          </div>
          {versions.length > 0 && (
            <div className="versions">
              <div className="fam-h sm">Versões anteriores</div>
              {versions.map(v => (
                <button key={v.id} type="button" onClick={() => nav(`/pecas/${v.id}`)}>
                  <span>{dateTime(v.createdAt)}</span><em data-s={v.status}>{v.status === 'ready' ? 'pronta' : v.status === 'failed' ? 'falhou' : 'gerando'}</em><Ic.ChevronRight />
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <ol className="phases">
            {PHASES.map(([p, label]) => {
              const seen = st.phasesSeen.includes(p);
              const now = st.status === 'running' && st.phase === p;
              const done = (seen && !now) || st.status === 'done';
              return (
                <li key={p} data-state={now ? 'now' : done ? 'done' : 'todo'}>
                  <span className="ph-dot">{done ? <Ic.Check /> : null}</span>
                  <span>{now && p === 'writing' && curIdx >= 0 ? `Escrevendo — ${unit} ${curIdx + 1} de ${sections.length}` : label}</span>
                </li>
              );
            })}
          </ol>

          {sections.length > 0 && (
            <div className="outline">
              <div className="fam-h sm">{meta.key === 'site' ? 'Páginas' : isPage ? 'Faixas da página' : 'Estrutura'}</div>
              <ol>
                {sections.map((s, i) => {
                  const state = st.status === 'done' || i < curIdx ? 'done' : i === curIdx ? 'now' : 'todo';
                  const pg = pages.find(x => x.id === s.id);
                  const go = () => {
                    if (pg && onSection) onSection(pg.slug);
                    else document.getElementById(`sec-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  };
                  return (
                    <li key={s.id} data-state={state} onClick={go}>
                      <span className="mono">{String(i + 1).padStart(2, '0')}</span><span>{s.title}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          <div className="feed-facts">
            <div><span>Blocos</span><b>{st.blocks.length}</b></div>
            <div><span>Tokens do modelo</span><b className="plain">{st.tokens.toLocaleString('pt-BR')}</b></div>
            <div><span>Custo</span><b>{meta.cost} tokens</b></div>
            <div><span>Tempo</span><b>{elapsed}s</b></div>
            {dsName && <div><span>Design system</span><b className="plain">{dsName}</b></div>}
          </div>
          {st.status === 'done' && <p className="saved-note"><Ic.Check />{isPage
            ? 'Salvo na Biblioteca. Revise no canvas — navegue pelas páginas e teste nos três tamanhos — e publique quando aprovar.'
            : 'Salvo na Biblioteca, com a versão do DNA e do design system que a geraram.'}</p>}
        </>
      )}
    </div>
  );
}
